import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  Database, 
  Eye, 
  Lock, 
  Mail, 
  Users, 
  Globe, 
  Scale, 
  ShieldCheck, 
  ShieldAlert, 
  ExternalLink,
  ChevronRight,
  Info,
  ArrowLeft
} from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen pt-24 pb-20 bg-white relative overflow-hidden font-sans">
      {/* Background Accents */}
      <div className="absolute top-0 left-0 w-full h-[600px] bg-[radial-gradient(circle_at_70%_0%,rgba(6,182,212,0.05)_0%,transparent_70%)] -z-10" />
      <div className="absolute bottom-0 left-0 w-full h-[400px] bg-[radial-gradient(circle_at_20%_100%,rgba(6,182,212,0.03)_0%,transparent_60%)] -z-10" />

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col items-start gap-4 mb-12"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] font-bold text-zinc-400 hover:text-cyan-600 transition-all group uppercase tracking-[0.2em]">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            Home
          </Link>
          
          <div className="space-y-2">
            <h1 className="text-3xl md:text-5xl font-black font-display text-cyan-950 tracking-tighter uppercase">
              Security & <span className="italic text-cyan-600">Privacy</span>
            </h1>
            <p className="text-zinc-500 font-medium text-sm leading-relaxed max-w-xl">
              Your trust is our foundation. This policy details how Fun Circle Games protects your digital footprint.
            </p>
          </div>

          <div className="flex items-center gap-4 text-[9px] font-bold uppercase tracking-[0.3em] text-cyan-400 pt-2 border-t border-cyan-600/10 w-full">
            <span>Last Updated: May 2, 2026</span>
            <span className="w-1 h-1 rounded-full bg-cyan-200" />
            <span>Version 2.0</span>
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 gap-8">
          {/* Section 1: Introduction */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 hover:shadow-cyan-900/10 transition-all group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-600/20 group-hover:-translate-y-1 transition-transform">
                <Info className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight flex items-center gap-3">
                  1. Introduction and Scope
                  <ChevronRight className="w-4 h-4 text-cyan-200 group-hover:translate-x-1 transition-transform" />
                </h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed">
                  <p>
                    Fun Circle Games ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website at <a href="https://funcirclegames.com" className="text-cyan-600 hover:underline inline-flex items-center gap-1 font-bold">funcirclegames.com <ExternalLink className="w-3 h-3" /></a> and use our simulation products and services (collectively, the "Services").
                  </p>
                  <p className="p-4 bg-cyan-50/50 rounded-xl border border-cyan-100/50 text-sm">
                    This policy applies to all users of our Services globally, including those in the United Kingdom (UK), European Economic Area (EEA), and the United States (US). We process personal data in accordance with the <strong>GDPR</strong>, the <strong>UK GDPR</strong>, the Data Protection Act 2018, and relevant US state privacy laws (such as the <strong>CCPA/CPRA</strong>).
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 2: Information We Collect */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 hover:shadow-cyan-900/10 transition-all"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-600/20">
                <Database className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6 w-full">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">2. Information We Collect</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-6 bg-cyan-50/30 rounded-3xl border border-cyan-100/50 space-y-4">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">A. Information You Provide Directly</h3>
                    <ul className="space-y-3">
                      {[
                        { label: 'Account Info', text: 'Name, email, username, and password provided during registration or wishlist joining.' },
                        { label: 'Uploaded Media', text: 'Audio files, MIDI recreations, and song assets used within Creator Studio.' },
                        { label: 'Payment Info', text: 'Transaction details via third-party partners (Stripe). We do not store full credit card data.' },
                        { label: 'Communication', text: 'Records of correspondence with our support team.' }
                      ].map((item, i) => (
                        <li key={i} className="text-sm font-medium text-zinc-700">
                          <strong className="text-cyan-900 block mb-0.5">{item.label}</strong>
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-6 bg-purple-50/30 rounded-3xl border border-purple-100/50 space-y-4">
                    <h3 className="text-xs font-black uppercase tracking-[0.2em] text-purple-600">B. Automatically Collected Information</h3>
                    <ul className="space-y-3">
                      {[
                        { label: 'Log & Usage Data', text: 'IP address, browser type, device identifiers, and OS details.' },
                        { label: 'Project Data', text: 'Simulation states, project metadata, and rendering configuration.' },
                        { label: 'Cookies & Tracking', text: 'Essential and analytical cookies to improve performance and experience.' }
                      ].map((item, i) => (
                        <li key={i} className="text-sm font-medium text-zinc-700">
                          <strong className="text-zinc-950 block mb-0.5">{item.label}</strong>
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 3: Purposes of Processing */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 transition-all"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <ShieldCheck className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">3. Purpose of Processing</h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed text-sm">
                  <p>We process your personal and media data for the following essential purposes:</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      'Facilitating cloud rendering and export of videos.',
                      'Synchronizing simulation projects across your devices.',
                      'Managing private storage and project backups.',
                      'Analyzing engine performance for optimization.',
                      'Processing subscription payments via Stripe/PayPal.',
                      'Responding to support and legal inquiries.'
                    ].map((item, i) => (
                      <li key={i} className="flex gap-4 p-4 bg-cyan-50/50 rounded-2xl border border-cyan-100 items-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                        <span className="font-bold text-cyan-950 text-xs">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 4: Legal Basis */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-cyan-50/20 p-8 md:p-10 rounded-[2rem] border border-cyan-100"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <Scale className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">4. Legal Basis (UK/EEA/Global)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { title: 'Contractual Necessity', desc: 'To provide the Services and cloud rendering you have requested.' },
                    { title: 'Legal Obligation', desc: 'To comply with tax, accounting, or security laws.' },
                    { title: 'Legitimate Interests', desc: 'To improve our physics engine, protect against fraud, and conduct analytics.' },
                    { title: 'Consent', desc: 'Where you have explicitly agreed to marketing or cookies.' }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 p-4 bg-white rounded-2xl border border-cyan-100">
                      <div className="w-2 h-2 rounded-full bg-cyan-600 mt-1.5 shrink-0" />
                      <div>
                        <span className="block text-[10px] font-black uppercase text-cyan-400 tracking-wider mb-1">{item.title}</span>
                        <p className="text-sm font-medium text-cyan-900">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 5: How We Use and Share Data */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 transition-all"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">5. Data Sharing & Third Parties</h2>
                <p className="text-zinc-600 font-medium text-sm">
                  We use trusted third-party providers to power Creator Studio. 
                  <strong className="text-cyan-900"> We do NOT sell your media assets or personal data.</strong>
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    'Cloud Storage (AWS/R2)',
                    'Rendering Clusters',
                    'Payment Gateways (Stripe/PayPal)',
                    'Email Infrastructure (Postmark/AWS)',
                    'Analytics (PostHog/Google)'
                  ].map((partner, i) => (
                    <span key={i} className="px-4 py-2 bg-cyan-50 rounded-full text-[10px] font-bold text-cyan-700 border border-cyan-100 uppercase tracking-widest">
                      {partner}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 6: International Transfers */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-zinc-50 p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <Globe className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">6. International Data Transfers</h2>
                <p className="font-medium text-cyan-900 opacity-90 leading-relaxed text-sm">
                  Our Services are hosted in the United States and other global regions. By using the Services, you acknowledge that your information may be transferred to and processed in countries where data protection laws may differ from those in your jurisdiction. We use Standard Contractual Clauses (SCCs) to ensure equivalent protection for UK/EEA residents.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Section 7: Security and Retention */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 hover:shadow-cyan-900/10 transition-all"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <Lock className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">7. Data Security and Retention</h2>
                <div className="p-6 bg-cyan-50/30 rounded-3xl border border-cyan-100/50">
                  <p className="text-zinc-700 font-medium leading-relaxed text-sm">
                    We implement robust technical and organizational measures (Encryption at rest/in transit, firewalls, and SSO) to protect your data. We retain your information only as long as necessary for the purposes of providing Services or as required by law (typically up to 7 years for financial records).
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 8: Your Rights */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 hover:shadow-cyan-900/10 transition-all"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <ShieldAlert className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6 w-full">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">8. Your Rights (GDPR & US State Laws)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { label: 'Access', text: 'Request a copy of your data in a machine-readable format.' },
                    { label: 'Rectification', text: 'Correct inaccurate or incomplete information.' },
                    { label: 'Erasure', text: 'Request deletion where no legal override exists.' },
                    { label: 'Opt-out', text: 'Object to certain processing or marketing.' },
                    { label: 'Non-Discrimination', text: 'US residents will not be penalized for rights usage.' }
                  ].map((right, i) => (
                    <div key={i} className="p-5 bg-cyan-50/20 rounded-2xl border border-cyan-100/50">
                      <span className="text-[10px] font-black uppercase text-cyan-400 tracking-wider block mb-1">{right.label}</span>
                      <p className="text-xs font-medium text-zinc-600 line-clamp-3">{right.text}</p>
                    </div>
                  ))}
                </div>
                <div className="p-4 bg-cyan-50 text-cyan-900 rounded-2xl text-center text-[10px] font-black uppercase tracking-widest border border-cyan-100">
                  Email support@funcirclegames.com featuring "Data Rights Request" to exercise these rights.
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 9: Children's Privacy */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 hover:shadow-cyan-900/10 transition-all"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">9. Children's Privacy</h2>
                <p className="text-zinc-600 font-medium leading-relaxed text-sm">
                  Fun Circle Games does not knowingly collect data from children under the age of 13 (or 16 in certain jurisdictions). If we discover a minor has provided us with personal data, we will delete it immediately. Parents can contact us at <a href="mailto:support@funcirclegames.com" className="text-cyan-600 font-bold hover:underline underline-offset-4">support@funcirclegames.com</a>.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Section 10: Contact Information */}
          <motion.section 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="pt-8"
          >
            <div className="bg-white p-10 md:p-14 rounded-[3rem] border border-cyan-100 shadow-2xl shadow-cyan-950/5 relative overflow-hidden">
              {/* Background Accent */}
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-cyan-500/5 rounded-full blur-[80px]" />
              
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-4xl font-heavy font-display text-cyan-950 uppercase tracking-tight leading-none">
                    10. Contact <br />
                    <span className="text-cyan-600/60">Information</span>
                  </h2>
                  <p className="text-zinc-500 font-medium text-sm">
                    Questions about your data or security? Our dedicated team is ready to assist you instantly.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                  <motion.a 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    href="mailto:support@funcirclegames.com"
                    className="flex items-center gap-6 p-6 bg-cyan-50 rounded-3xl border border-cyan-100 shadow-xl hover:bg-cyan-100 transition-colors"
                  >
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-cyan-100">
                      <Mail className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <span className="block text-[10px] font-black uppercase text-cyan-400 tracking-[0.2em] mb-1">Support Team</span>
                      <span className="text-lg font-bold text-cyan-950">support@funcirclegames.com</span>
                    </div>
                  </motion.a>

                  <motion.a 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    href="mailto:contact@funcirclegames.com"
                    className="flex items-center gap-6 p-6 bg-white rounded-3xl border border-cyan-100 shadow-sm hover:bg-cyan-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-cyan-50 rounded-2xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <span className="block text-[10px] font-black uppercase text-cyan-400 tracking-[0.2em] mb-1">General Inquiries</span>
                      <span className="text-cyan-950 font-bold text-lg">contact@funcirclegames.com</span>
                    </div>
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
